import { Alert, Avatar, Spin } from 'antd'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import {
  ArrowRightOutlined,
  EditOutlined,
  UserOutlined,
} from '@ant-design/icons'

import { ITimelineRow, TemplateCard } from 'src/interface/org'
import { useTemplateProjectId } from 'src/query/useTemplateProjectId'
import { useMigrateValidate } from 'src/query/orgMigrate'
import { ValidateRes } from 'src/service/OrgSyncService'
import MigrateChangeDiscipline, {
  IChangeDiscipline,
} from './MigrateChangeDiscipline'
import { useStableCallback } from 'src/hooks/useStableCallback'
import useMigrateStore from './useMigrateStore'
import { useMainProcesses, useMilestones } from 'src/query/taskCtrl'
import MigrateChangeProcessMilestone, {
  IChangeMilestoneProcess,
} from './MigrateProcessMilestone'

interface MigrateValidateItemsProps {
  canMove: (canMove: boolean) => void
}

const MigrateValidateItems: FC<MigrateValidateItemsProps> = ({ canMove }) => {
  const [migrateRes, setMigrateRes] = useState<ValidateRes>()
  const [changeDiscipline, setChangeDiscipline] = useState<IChangeDiscipline>({
    show: false,
  })
  const [changeProcessMilestone, setChangeProcess] =
    useState<IChangeMilestoneProcess>({
      show: false,
    })

  const { setMigrateRes: storeRes } = useMigrateStore()

  const { selectedTemplate, templateProject, destinationProject } =
    useTemplateProjectId()

  const { data: mainProcess = [] } = useMainProcesses(templateProject?.id)
  const { data: milestones = [] } = useMilestones(templateProject?.id)

  const { users: rowUsers, disciplines: rowDisciplines } = useMemo(() => {
    const rows: ITimelineRow[] = selectedTemplate?.template?.rows || []
    const users = Array.from(
      new Set(rows.flatMap((r) => r.responsible).filter(Boolean)),
    )
    const disciplines = rows.flatMap((r) => ({
      ...r.disciplines,
      user: r.responsible,
    }))
    return { users, disciplines }
  }, [selectedTemplate])

  const extractIds = (
    cards: TemplateCard[],
    key: 'mile_stone' | 'mainProcess',
  ): number[] => {
    return Array.from(
      new Set(
        cards
          ?.map((c) => c[key]?.id)
          .filter((i): i is number => typeof i === 'number'),
      ),
    )
  }

  const { data, isLoading } = useMigrateValidate({
    discipline_ids: rowDisciplines.map((d) => d.id),
    user_ids: rowUsers.map((u) => u?.id ?? -1),
    project_id: templateProject?.id || 0,
    destination_project_id: destinationProject?.id || 0,
    mile_stone_ids: extractIds(selectedTemplate?.cards || [], 'mile_stone'),
    main_process_ids: extractIds(selectedTemplate?.cards || [], 'mainProcess'),
  })

  useEffect(() => {
    if (data) setMigrateRes(data)
  }, [data])

  const stableCanMove = useStableCallback(canMove)
  const stableStoreRes = useStableCallback(storeRes)

  useEffect(() => {
    if (!migrateRes) return

    const hasValidDisciplines = Object.values(
      migrateRes.disciplines || {},
    ).every((d) => d && d.id)
    const hasValidUsers = Object.values(migrateRes.users || {}).every(
      (u) => u && u.id,
    )
    const hasValidProcesses = Object.entries(migrateRes.main_processes || {})
      .filter(([p]) => p && +p > 0)
      .every(([, p]) => p && p.id)
    const hasValidMilestones = Object.entries(migrateRes.mile_stones || {})
      .filter(([p]) => p && +p > 0)
      .every(([, p]) => p && p.id)
    stableStoreRes(migrateRes)
    stableCanMove(
      hasValidDisciplines &&
        hasValidUsers &&
        hasValidProcesses &&
        hasValidMilestones,
    )
  }, [migrateRes, stableCanMove, stableStoreRes])

  const getNewDiscipline = useCallback(
    (oldId: number) => {
      const discipline = migrateRes?.disciplines?.[oldId]
      const oldUser = rowDisciplines.find((r) => r.id === oldId)?.user
      const newUser = oldUser && migrateRes?.users?.[oldUser.id]
      return discipline ? { ...discipline, user: newUser } : undefined
    },
    [migrateRes, rowDisciplines],
  )

  const handleSave = useCallback((updated?: ValidateRes) => {
    if (!updated) return
    setMigrateRes((prev) => ({
      disciplines: { ...prev?.disciplines, ...updated.disciplines },
      users: { ...prev?.users, ...updated.users },
      mile_stones: { ...prev?.mile_stones, ...updated.mile_stones },
      main_processes: {
        ...prev?.main_processes,
        ...updated.main_processes,
      },
    }))
    setChangeDiscipline({ show: false })
    setChangeProcess({ show: false })
  }, [])

  return (
    <div className="w-full h-full max-h-[95%] overflow-x-hidden flex flex-col gap-2">
      <Spin size="large" spinning={isLoading} tip="Validating Items...">
        <h1 className="h3 py-4">Disciplines</h1>
        <div className="w-full flex mx-4 flex-wrap gap-3 gap-x-16 mb-2">
          {rowDisciplines.map((d) => {
            const next = getNewDiscipline(d.id)
            return (
              <div
                key={d.id}
                className="app-card !m-0 min-w-[500px] flex gap-2 gap-x-12"
                style={{ ['--accent-color' as string]: d.color }}
              >
                <div className="flex flex-col gap-2 w-1/2">
                  <h1 className="app-card--title">{d.name}</h1>
                  <h1 className="app-card--description p-medium">
                    {templateProject?.projectName}
                  </h1>
                  <div className="flex gap-2 items-center">
                    <Avatar
                      shape="square"
                      size="small"
                      src={d.user?.photoUrl}
                      icon={!d.user?.photoUrl && <UserOutlined />}
                    />
                    <span>{d.user?.name}</span>
                  </div>
                </div>

                <ArrowRightOutlined />

                <div
                  onClick={() =>
                    setChangeDiscipline({
                      show: true,
                      newDisciplineId: next?.id,
                      newResponsibleId: next?.user?.id,
                      oldDisciplineId: d.id,
                      oldUserId: d.user?.id,
                    })
                  }
                  className="w-1/2 px-1 relative hover:bg-slate-400 p-1 hover:scale-105 hover:rounded hover:bg-opacity-10 cursor-pointer flex flex-col justify-center items-end"
                >
                  <div className="absolute -scale-x-100 top-0 left-0 p-1">
                    <EditOutlined style={{ color: '#f5222d' }} />
                  </div>
                  <div>
                    {next ? (
                      <div className="flex flex-col gap-2 justify-end">
                        <h1 className="app-card--title flex gap-2 justify-end">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            className="app-card--app-logo"
                          >
                            <circle
                              r="12"
                              cx="12"
                              cy="12"
                              fill={next.color ?? '#000'}
                            />
                          </svg>
                          {next.shortName}
                        </h1>
                        <h1 className="app-card--description flex p-medium justify-end">
                          {destinationProject?.projectName}
                        </h1>
                        {next.user ? (
                          <div className="flex gap-2 items-center justify-end">
                            <Avatar
                              shape="square"
                              size="small"
                              src={next.user.image_url}
                              icon={!next.user.image_url && <UserOutlined />}
                            />
                            <span>
                              {next.user.firstName} {next.user.lastName}
                            </span>
                          </div>
                        ) : (
                          <Alert message="User Missing" type="error" />
                        )}
                      </div>
                    ) : (
                      <Alert message="Discipline Missing" type="error" />
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
        {Object.keys(migrateRes?.main_processes || {}).length > 0 && (
          <>
            <hr />
            <h1 className="h3 py-4">Main Processes</h1>
          </>
        )}
        <div className="w-full flex mx-4 flex-wrap gap-3 gap-x-16 mb-2">
          {Object.entries(migrateRes?.main_processes || {}).map(
            ([id, process]) => {
              if (id === null || id === undefined || +id < 1) return null
              const oldMainProcess = mainProcess.find((m) => m.id === +id)
              return (
                <div
                  key={id}
                  className="app-card !m-0 min-w-[500px] flex gap-2 gap-x-12"
                >
                  <div className="flex flex-col gap-2 w-1/2">
                    <h1 className="app-card--title">{oldMainProcess?.name}</h1>
                    <h1 className="app-card--description p-medium">
                      {templateProject?.projectName}
                    </h1>
                  </div>
                  <ArrowRightOutlined />
                  <div
                    onClick={() =>
                      setChangeProcess({
                        show: true,
                        type: 'process',
                        oldProcess: +id,
                        newProcess: process?.id,
                      })
                    }
                    className="w-1/2  px-1 relative hover:bg-slate-400 break-keep whitespace-nowrap p-1  hover:rounded hover:bg-opacity-10 cursor-pointer flex flex-col justify-center items-end"
                  >
                    <div className="absolute -scale-x-100 top-0 left-0 p-1">
                      <EditOutlined style={{ color: '#f5222d' }} />
                    </div>
                    <div className="flex flex-col whitespace-nowrap gap-2 justify-center items-end">
                      {process ? (
                        <h1 className="app-card--title">{process?.name}</h1>
                      ) : (
                        <Alert message="Main Process Missing" type="error" />
                      )}
                      <h1 className="app-card--description p-medium">
                        {destinationProject?.projectName}
                      </h1>
                    </div>
                  </div>
                </div>
              )
            },
          )}
        </div>
        {Object.keys(migrateRes?.mile_stones || {}).length > 0 && (
          <>
            <hr />
            <h1 className="h3 py-4">Milestones</h1>
          </>
        )}
        <div className="w-full flex mx-4 flex-wrap gap-3 gap-x-16 mb-2">
          {Object.entries(migrateRes?.mile_stones || {}).map(([id, mile]) => {
            if (id === null || id === undefined || +id < 1) return null
            const oldMilestone = milestones.find((m) => m.id === +id)
            return (
              <div
                key={id}
                className="app-card !m-0 min-w-[500px] flex gap-2 gap-x-12"
                style={{ ['--accent-color' as string]: '#FFC107' }}
              >
                <div className="flex flex-col gap-2 w-1/2">
                  <h1 className="app-card--title">{oldMilestone?.name}</h1>
                  <h1 className="app-card--description p-medium">
                    {templateProject?.projectName}
                  </h1>
                </div>
                <ArrowRightOutlined />
                <div
                  onClick={() =>
                    setChangeProcess({
                      show: true,
                      type: 'milestone',
                      oldMilestone: +id,
                      newMilestone: mile?.id,
                    })
                  }
                  className="w-1/2 break-keep p-1  px-1 relative hover:bg-slate-400 hover:rounded hover:bg-opacity-10 cursor-pointer flex flex-col justify-center items-end"
                >
                  <div className="absolute -scale-x-100 top-0 left-0 p-1">
                    <EditOutlined style={{ color: '#f5222d' }} />
                  </div>
                  <div className="flex flex-col whitespace-nowrap gap-2 justify-end">
                    {mile ? (
                      <h1 className="app-card--title">{mile?.name}</h1>
                    ) : (
                      <Alert message="Milestone Missing" type="error" />
                    )}
                    <h1 className="app-card--description p-medium">
                      {destinationProject?.projectName}
                    </h1>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </Spin>
      <MigrateChangeDiscipline
        {...changeDiscipline}
        projectId={destinationProject?.id ?? -1}
        onClose={() => setChangeDiscipline({ show: false })}
        onSave={handleSave}
      />
      <MigrateChangeProcessMilestone
        {...changeProcessMilestone}
        projectId={destinationProject?.id ?? -1}
        onClose={() => setChangeProcess({ show: false })}
        onSave={handleSave}
      />
    </div>
  )
}

export default MigrateValidateItems
